import { avatarSizeToPx } from "@components/Avatar";
import {
  CauseIllustrationProps,
  CauseIllustrationSize,
  categoryIllustrationSizes,
  FIT_PARENT,
} from "@components/CauseIllustration/types";
import { css } from "@emotion/react";
import type { CSSInterpolation } from "@emotion/serialize";
import React, { type JSX } from "react";

import { CauseCategory } from "@every.org/common/src/entity/types";

import { ReactComponent as AnimalsIllustration } from "src/assets/illustrations/animals.svg";
import { ReactComponent as ArtsIllustration } from "src/assets/illustrations/arts_culture_humanities.svg";
import { ReactComponent as EducationIllustration } from "src/assets/illustrations/education.svg";
import { ReactComponent as EnvironmentIllustration } from "src/assets/illustrations/environment.svg";
import { ReactComponent as GrayAnimalsIllustration } from "src/assets/illustrations/gray/animals.svg";
import { ReactComponent as GrayArtsIllustration } from "src/assets/illustrations/gray/arts_culture_humanities.svg";
import { ReactComponent as GrayEducationIllustration } from "src/assets/illustrations/gray/education.svg";
import { ReactComponent as GrayEnvironmentIllustration } from "src/assets/illustrations/gray/environment.svg";
import { ReactComponent as GrayHealthIllustration } from "src/assets/illustrations/gray/health.svg";
import { ReactComponent as GrayHumanRightsIllustration } from "src/assets/illustrations/gray/human_and_civil_rights.svg";
import { ReactComponent as GrayHumanServicesIllustration } from "src/assets/illustrations/gray/human_services.svg";
import { ReactComponent as GrayReligionIllustration } from "src/assets/illustrations/gray/religion.svg";
import { ReactComponent as GrayResearchIllustration } from "src/assets/illustrations/gray/research_and_public_policy.svg";
import { ReactComponent as HealthIllustration } from "src/assets/illustrations/health.svg";
import { ReactComponent as HumanRightsIllustration } from "src/assets/illustrations/human_and_civil_rights.svg";
import { ReactComponent as HumanServicesIllustration } from "src/assets/illustrations/human_services.svg";
import { ReactComponent as ReligionIllustration } from "src/assets/illustrations/religion.svg";
import { ReactComponent as ResearchIllustration } from "src/assets/illustrations/research_and_public_policy.svg";

type SvgComponent = (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
const CATEGORY_ILLUSTRATIONS: {
  [key in CauseIllustrationProps["cause"]]: SvgComponent;
} = {
  [CauseCategory.ANIMALS]: AnimalsIllustration,
  [CauseCategory.ARTS_CULTURE_HUMANITIES]: ArtsIllustration,
  [CauseCategory.EDUCATION]: EducationIllustration,
  [CauseCategory.ENVIRONMENT]: EnvironmentIllustration,
  [CauseCategory.HEALTH]: HealthIllustration,
  [CauseCategory.HUMAN_AND_CIVIL_RIGHTS]: HumanRightsIllustration,
  [CauseCategory.HUMAN_SERVICES]: HumanServicesIllustration,
  [CauseCategory.RELIGION]: ReligionIllustration,
  [CauseCategory.RESEARCH_AND_PUBLIC_POLICY]: ResearchIllustration,
};

const GRAY_CATEGORY_ILLUSTRATIONS: {
  [key in CauseIllustrationProps["cause"]]: SvgComponent;
} = {
  [CauseCategory.ANIMALS]: GrayAnimalsIllustration,
  [CauseCategory.ARTS_CULTURE_HUMANITIES]: GrayArtsIllustration,
  [CauseCategory.EDUCATION]: GrayEducationIllustration,
  [CauseCategory.ENVIRONMENT]: GrayEnvironmentIllustration,
  [CauseCategory.HEALTH]: GrayHealthIllustration,
  [CauseCategory.HUMAN_AND_CIVIL_RIGHTS]: GrayHumanRightsIllustration,
  [CauseCategory.HUMAN_SERVICES]: GrayHumanServicesIllustration,
  [CauseCategory.RELIGION]: GrayReligionIllustration,
  [CauseCategory.RESEARCH_AND_PUBLIC_POLICY]: GrayResearchIllustration,
};

const FIT_PARENT_CSS = css`
  height: 100%;
  width: auto;
`;
const ILLUSTRATION_SIZE_CSS = Object.fromEntries(
  [...categoryIllustrationSizes].map((size) => [
    size,
    css`
      height: ${avatarSizeToPx[size]}px;
      width: auto;
    `,
  ])
) as { [key in CauseIllustrationSize]: CSSInterpolation };

export default function Illustration({
  cause,
  grayscale = false,
  size,
  ...rest
}: CauseIllustrationProps) {
  const IllustrationAsset = (
    grayscale ? GRAY_CATEGORY_ILLUSTRATIONS : CATEGORY_ILLUSTRATIONS
  )[cause];
  return (
    <IllustrationAsset
      css={size === FIT_PARENT ? FIT_PARENT_CSS : ILLUSTRATION_SIZE_CSS[size]}
      {...rest}
    />
  );
}
